import { useState } from 'react';
import { api } from '../../lib/api';
import { useHeldResource } from '../../lib/hooks';

/**
 * The client's CSAT from Autotask's ticket survey, drawn the way the
 * screenshots on the review's User Feedback slide used to show it: the survey
 * table and the "Avg Survey Score Past 12 Mo" dial. Used by the slide
 * (AmSbrPresentation) and by the review form's preview of it.
 * Data: GET /am/clients/:id/autotask/csat (routes/amAutotask.ts).
 */

/** The twelve months up to today, the window the CSAT slide captures. */
export function csatWindow() {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  to.setDate(to.getDate() + 1);
  const from = new Date(to);
  from.setMonth(from.getMonth() - 12);
  return { from, to };
}

export interface CsatResponse {
  id: number;
  ticketNumber: string | null;
  title: string | null;
  survey: string | null;
  surveyScore: number | null;
  accountScore: number | null;
  contactScore: number | null;
  resourceScore: number | null;
  completed: string | null;
}
/** What the User Feedback slide keeps: the window and what Autotask returned. */
export interface CsatSnap {
  from: string;
  to: string;
  linked: boolean;
  companyName: string | null;
  average: number | null;
  count: number;
  responses: CsatResponse[];
}

export async function loadCsat(clientId: string, w: { from: Date; to: Date }, refresh: boolean): Promise<CsatSnap> {
  const r = await api.get<Omit<CsatSnap, 'from' | 'to'>>(
    `/am/clients/${clientId}/autotask/csat?${new URLSearchParams({ from: w.from.toISOString(), to: w.to.toISOString(), ...(refresh ? { refresh: '1' } : {}) })}`,
  );
  return { ...r, from: w.from.toISOString(), to: w.to.toISOString() };
}

const score = (v: number | null) => (v === null ? '—' : v.toFixed(2));

/**
 * The "Avg Survey Score Past 12 Mo" dial, drawn the way Autotask's widget
 * draws it: a half circle from 0 to 5 in three bands (red, blue, light blue),
 * a needle at the average, and the figure under it.
 */
export function CsatDial({ value }: { value: number | null }) {
  const cx = 100;
  const cy = 92;
  const r = 70;
  const w = 26;
  const pt = (v: number, rad: number) => {
    const a = Math.PI * (1 - Math.max(0, Math.min(5, v)) / 5);
    return [cx + rad * Math.cos(a), cy - rad * Math.sin(a)] as const;
  };
  const band = (a: number, b: number) => {
    const [x1, y1] = pt(a, r);
    const [x2, y2] = pt(b, r);
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
  };
  const v = value ?? 0;
  const [nx, ny] = pt(v, r + 6);
  const a = Math.PI * (1 - Math.max(0, Math.min(5, v)) / 5);
  const px = Math.sin(a) * 5;
  const py = Math.cos(a) * 5;
  return (
    <svg viewBox="0 0 200 120" className="am-csat-dial" role="img" aria-label={`Average survey score ${value === null ? 'none' : value.toFixed(2)} out of 5`}>
      <path d={band(0, 5 / 3)} stroke="#f05a42" strokeWidth={w} fill="none" />
      <path d={band(5 / 3, 10 / 3)} stroke="#3a62d6" strokeWidth={w} fill="none" />
      <path d={band(10 / 3, 5)} stroke="#a9bce8" strokeWidth={w} fill="none" />
      {value !== null && <polygon points={`${cx + px},${cy - py} ${cx - px},${cy + py} ${nx},${ny}`} fill="#111" />}
      {value !== null && <circle cx={cx} cy={cy} r={6} fill="#111" />}
      <text x={cx - r} y={cy + 20} textAnchor="middle" className="am-csat-dial-end">0</text>
      <text x={cx + r} y={cy + 20} textAnchor="middle" className="am-csat-dial-end">5</text>
      <text x={cx - r + 8} y={cy + 24} dx={22} textAnchor="start" className="am-csat-dial-value">
        {value === null ? '—' : value.toFixed(2)}
      </text>
    </svg>
  );
}

/** The survey table — ticket, title, survey, and the four scores to two decimals. */
export function CsatTable({ rows }: { rows: CsatResponse[] }) {
  return (
    <table className="am-csat-table">
      <thead>
        <tr>
          <th>Ticket / Task Number</th>
          <th>Ticket / Task Title</th>
          <th>Survey Name</th>
          <th className="num">Survey Score</th>
          <th className="num">Account Score</th>
          <th className="num">Contact Survey Score</th>
          <th className="num">Resource Survey Score</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td className="am-csat-link">{r.ticketNumber ?? '—'}</td>
            <td className="am-csat-link">{r.title ?? '—'}</td>
            <td className="am-csat-link">{r.survey ?? '—'}</td>
            <td className="num">{score(r.surveyScore)}</td>
            <td className="num">{score(r.accountScore)}</td>
            <td className="num">{score(r.contactScore)}</td>
            <td className="num">{score(r.resourceScore)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The review form's preview of the User Feedback slide: the selected client's
 * survey results for the last twelve months and the dial, read from Autotask
 * now — so whoever sets up the review sees what the slide will show without
 * uploading anything. The slide captures its own copy when it is presented.
 */
export function CsatPreview({ clientId }: { clientId: string }) {
  const [w] = useState(csatWindow);
  const data = useHeldResource<Omit<CsatSnap, 'from' | 'to'>>(
    `/am/clients/${clientId}/autotask/csat?${new URLSearchParams({ from: w.from.toISOString(), to: w.to.toISOString() })}`,
  );
  const d = data.data;
  if (data.loading && !d) return <p className="small muted">Reading this client's survey results from Autotask…</p>;
  if (data.error) return <p className="small muted">Could not read the survey results from Autotask: {data.error}</p>;
  if (!d) return null;
  if (!d.linked) {
    return (
      <p className="small muted">
        This client is not linked to an Autotask company, so the slide has no survey results to show — the screenshots below are
        used instead.
      </p>
    );
  }
  return (
    <div className="am-csat-preview">
      <p className="xs muted">
        Filled automatically from Autotask for {d.companyName}: {d.count} survey response{d.count === 1 ? '' : 's'} in the last twelve
        months. The slide keeps its own copy from the day it is presented.
      </p>
      <div className="am-csat">
        <div className="am-csat-main">
          {d.responses.length === 0 ? (
            <p className="am-csat-empty">No survey responses in the last twelve months.</p>
          ) : (
            <div className="am-csat-table-wrap">
              <CsatTable rows={d.responses} />
            </div>
          )}
        </div>
        <div className="am-csat-side">
          <div className="am-csat-side-title">Avg Survey Score Past 12 Mo</div>
          <div className="am-csat-side-body">
            <CsatDial value={d.average} />
            <span className="am-csat-side-label">Company</span>
          </div>
        </div>
      </div>
    </div>
  );
}
